"use client";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import type { IResource } from "@gettactic/api";
import { cn } from "@gettactic/helpers/src/css/cn";
import { ChevronDown, Users } from "lucide-react";
import React from "react";

const rowBox =
  "flex items-center gap-3 rounded-[10px] border-[0.5px] border-[rgba(20,29,38,0.1)] bg-[#fbfcfd] px-[13px] py-[11px]";

type Assignee = { id: string; name: string; profile_photo_url?: string | null };

/** Circular tile with a group glyph, used to represent a team. */
const TeamGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <div
    className={cn(
      "flex items-center justify-center rounded-full bg-indigo-50 text-primary",
      className
    )}
  >
    <Users className="h-4 w-4" aria-hidden="true" />
  </div>
);

const AssigneeRow: React.FC<{
  name: string;
  subtitle?: string;
  avatar: React.ReactNode;
}> = ({ name, subtitle, avatar }) => (
  <div className={rowBox}>
    {avatar}
    <div className="min-w-0">
      <p className="truncate font-inter font-semibold text-[14px] text-neutral-900">
        {name}
      </p>
      {subtitle ? (
        <p className="truncate font-inter text-[#778086] text-[12px]">
          {subtitle}
        </p>
      ) : null}
    </div>
  </div>
);

interface ResourceAssignmentsProps {
  resource: Pick<
    IResource,
    | "visible_assignment"
    | "user_visible_assignments"
    | "team_visible_assignments"
  >;
}

/**
 * Shows a resource's assignment on the click popover: a single owner card, or —
 * when several people/teams share the desk — an expandable "Multiple assignees"
 * aggregator listing each team and person.
 */
export const ResourceAssignments: React.FC<ResourceAssignmentsProps> = ({
  resource
}) => {
  const teams = resource.team_visible_assignments ?? [];
  const users = resource.user_visible_assignments ?? [];
  const owner = resource.visible_assignment ?? null;
  const total = teams.length + users.length;
  const isMultiple = total > 1;

  const [expanded, setExpanded] = React.useState(false);

  if (!isMultiple) {
    const person: Assignee | null =
      owner ?? (users.length === 1 ? users[0] : null);
    const team: Assignee | null =
      !person && teams.length === 1 ? teams[0] : null;
    if (!person && !team) {
      return null;
    }
    return (
      <div className="mt-3">
        {person ? (
          <AssigneeRow
            name={person.name}
            subtitle="Assigned owner"
            avatar={
              <UserAvatar
                user={person}
                size="s"
                className="h-10 w-10 ring-1 ring-neutral-300"
              />
            }
          />
        ) : team ? (
          <AssigneeRow
            name={team.name}
            subtitle="Assigned team"
            avatar={<TeamGlyph className="h-10 w-10" />}
          />
        ) : null}
      </div>
    );
  }

  const summary = [
    teams.length
      ? `${teams.length} ${teams.length === 1 ? "team" : "teams"}`
      : null,
    users.length
      ? `${users.length} ${users.length === 1 ? "person" : "people"}`
      : null
  ]
    .filter(Boolean)
    .join(" · ");

  const clusterPeople = users.slice(0, 2);

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className={cn(rowBox, "w-full text-left")}
      >
        <div className="flex items-center">
          {clusterPeople.map((user, index) => (
            <UserAvatar
              key={user.id}
              user={user}
              size="sm"
              className={cn("ring-2 ring-[#fbfcfd]", index > 0 && "-ml-2")}
            />
          ))}
          <TeamGlyph
            className={cn(
              "h-8 w-8 ring-2 ring-[#fbfcfd]",
              clusterPeople.length > 0 && "-ml-2"
            )}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-inter font-semibold text-[#040d28] text-[14px] leading-[1.2]">
            Multiple assignees
          </p>
          <p className="truncate font-inter text-[#778086] text-[12px]">
            {summary}
          </p>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-[160ms]",
            expanded && "rotate-180"
          )}
          aria-hidden="true"
        />
      </button>
      {expanded ? (
        <div className="mt-1.5 flex max-h-64 flex-col gap-1.5 overflow-y-auto">
          {teams.map((team) => (
            <AssigneeRow
              key={team.id}
              name={team.name}
              subtitle="Team"
              avatar={<TeamGlyph className="h-8 w-8" />}
            />
          ))}
          {users.map((user) => (
            <AssigneeRow
              key={user.id}
              name={user.name}
              avatar={<UserAvatar user={user} size="sm" />}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};
